import React from 'react';
import moment from 'moment';
import { Table, Tooltip, Button, Icon, Card, Badge, Dropdown, Menu } from 'antd';
import { useState } from 'react';

const tableStyles = `
@keyframes po-beat   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.22)} }
@keyframes po-glow   { 0%,100%{box-shadow:0 0 0 0 var(--gc,rgba(24,144,255,.35))} 50%{box-shadow:0 0 0 8px rgba(0,0,0,0)} }
@keyframes po-spin   { 0%{transform:rotate(0)} 100%{transform:rotate(180deg)} }
@keyframes po-bar    { 0%{background-position:0% center} 100%{background-position:200% center} }

/* ── Row stripe ── */
.po-row-stripe td { background:#f7f9ff!important; }

/* ── Table card ── */
.po-card { border-radius:10px!important; box-shadow:0 2px 12px rgba(24,144,255,0.07)!important; border:1px solid #e6f0ff!important; overflow:hidden; }
.po-card-header { padding:14px 20px 12px; border-bottom:1px solid #f0f5ff; display:flex; align-items:center; gap:8px; }

/* ── Table overrides ── */
.po-table .ant-table-thead > tr > th { background:#f0f7ff!important; font-size:11px!important; font-weight:700!important; color:#1890ff!important; letter-spacing:1px!important; text-transform:uppercase!important; border-bottom:2px solid #d6e8ff!important; white-space:nowrap; }
.po-table .ant-table-tbody > tr > td { font-size:12px!important; color:#262626!important; font-weight:500!important; border-color:#e6f0ff!important; padding:8px 12px!important; }
.po-table .ant-table-bordered .ant-table-thead > tr > th,
.po-table .ant-table-bordered .ant-table-tbody > tr > td { border-color:#e6f0ff!important; }
.po-table .ant-table-tbody > tr:hover > td { background:#e6f7ff!important; }
.po-table .ant-spin-dot-item { background:#1890ff!important; }
.po-table .ant-pagination-item-active { border-color:#1890ff!important; background:#1890ff!important; }
.po-table .ant-pagination-item-active a { color:#fff!important; }
.po-table .ant-pagination { padding:8px 16px!important; }

/* ── Status badges ── */
.po-badge { display:inline-flex; align-items:center; gap:4px; padding:2px 9px; border-radius:10px; font-size:11px; font-weight:700; white-space:nowrap; }
.po-dot   { width:6px; height:6px; border-radius:50%; flex-shrink:0; }
.po-open   { background:#f6ffed; border:1px solid #b7eb8f; color:#389e0d; }
.po-open   .po-dot { background:#52c41a; box-shadow:0 0 5px #52c41a; }
.po-served { background:#e6fffb; border:1px solid #87e8de; color:#08979c; }
.po-served .po-dot { background:#13c2c2; box-shadow:0 0 5px #13c2c2; }

/* ── Currency badge ── */
.po-currency { display:inline-flex; align-items:center; gap:4px; padding:2px 8px; border-radius:6px; font-size:11px; font-weight:700; }
.po-currency-php { background:#fff7e6; border:1px solid #ffd591; color:#d46b08; }
.po-currency-usd { background:#f9f0ff; border:1px solid #d3adf7; color:#531dab; }

/* ── Items chip ── */
.po-items-chip { display:inline-flex; align-items:center; gap:6px; padding:3px 10px; border-radius:8px; background:#f0f7ff; border:1px solid #d6e8ff; font-size:11px; font-weight:700; color:#0050b3; }
.po-items-sep  { color:#d6e8ff; font-weight:400; }

/* ── PO number ── */
.po-num { font-size:12px; font-weight:700; color:#0050b3; letter-spacing:.3px; }

/* ── Date chip ── */
.po-date-chip { display:inline-flex; align-items:center; gap:5px; padding:2px 8px; border-radius:6px; background:#f9f0ff; border:1px solid #d3adf7; font-size:11px; font-weight:600; color:#531dab; }

/* ── Customer label ── */
.po-customer { font-size:12px; font-weight:600; color:#262626; }

/* ── Action button ── */
.po-settings-btn { width:28px!important; height:28px!important; min-width:28px!important; padding:0!important; border-radius:7px!important; display:inline-flex!important; align-items:center!important; justify-content:center!important; font-size:13px!important; border:1.5px solid #d6e8ff!important; background:#e6f7ff!important; color:#1890ff!important; transition:all .18s!important; }
.po-settings-btn.open { background:#1890ff!important; color:#fff!important; border-color:#1890ff!important; box-shadow:0 3px 10px rgba(24,144,255,.35)!important; }
.po-settings-btn.open .anticon-setting { animation:po-spin .4s cubic-bezier(.22,1,.36,1) forwards!important; }
.po-action-menu.ant-dropdown-menu { border-radius:10px!important; border:1px solid #e6f0ff!important; box-shadow:0 8px 28px rgba(24,144,255,.12)!important; padding:5px!important; min-width:160px!important; }
.po-action-menu .ant-dropdown-menu-item { border-radius:7px!important; padding:8px 12px!important; font-size:12px!important; font-weight:600!important; transition:background .15s,transform .12s!important; }
.po-action-menu .ant-dropdown-menu-item:hover { transform:translateX(3px)!important; background:#e6f7ff!important; color:#1890ff!important; }
`;

/* ── Status Badge ── */
const StatusBadge = ({ status }) => {
  const map = {
    OPEN:   { cls: 'po-open',   label: 'OPEN'   },
    SERVED: { cls: 'po-served', label: 'SERVED' },
  };
  const cfg = map[status] || { cls: 'po-open', label: status };
  return (
    <span className={`po-badge ${cfg.cls}`}>
      <span className="po-dot" />{cfg.label}
    </span>
  );
};

/* ── Currency Badge ── */
const CurrencyBadge = ({ currency }) => {
  const cls = currency === 'USD' ? 'po-currency-usd' : 'po-currency-php';
  return <span className={`po-currency ${cls}`}>{currency}</span>;
};

/* ── Action Cell ── */
const ActionCell = ({ record, openModal }) => {
  const [open, setOpen] = useState(false);
  const menu = (
    <Menu className="po-action-menu">
      <Menu.Item key="view" onClick={() => openModal(record)}>
        <Icon type="profile" style={{ marginRight: 8, color: '#1890ff' }} />
        View PO Details
      </Menu.Item>
    </Menu>
  );
  return (
    <Tooltip placement="right" title="View purchase order details">
      <Dropdown
        overlay={menu}
        trigger={['hover']}
        placement="bottomLeft"
        getPopupContainer={() => document.body}
        onVisibleChange={v => setOpen(v)}
      >
        <Button
          className={`po-settings-btn${open ? ' open' : ''}`}
          size="small"
          icon="setting"
        />
      </Dropdown>
    </Tooltip>
  );
};

/* ── Main Component ── */
const PurchaseOrderTable = ({
  poList,
  customers,
  pageLength,
  paginate,
  onSizeChange,
  onPageChange,
  openModal,
}) => {
  const openCount   = (poList || []).filter(d => d.status === 'OPEN').length;
  const servedCount = (poList || []).filter(d => d.status === 'SERVED').length;

  const tableColumns = [
    {
      key: 'viewPo',
      align: 'center',
      width: 46,
      render: data => <ActionCell record={data} openModal={openModal} />,
    },
    {
      key: 'status',
      title: 'Status',
      align: 'center',
      width: 120,
      render: d => <StatusBadge status={d.status} />,
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      filters: [
        { text: 'OPEN',   value: 'OPEN'   },
        { text: 'SERVED', value: 'SERVED' },
      ],
      onFilter: (value, record) => record.status.indexOf(value) === 0,
    },
    {
      key: 'po_num',
      title: 'PO No.',
      dataIndex: 'po_num',
      align: 'center',
      render: val => <span className="po-num">{val}</span>,
    },
    {
      key: 'date',
      title: 'Date Issued',
      dataIndex: 'date',
      align: 'center',
      render: val => (
        <span className="po-date-chip">
          <Icon type="calendar" style={{ fontSize: 10 }} />{val}
        </span>
      ),
      sorter: (a, b) =>
        moment(a.date || '').unix() - moment(b.date || '').unix(),
    },
    {
      key: 'customer',
      title: 'Customer',
      dataIndex: 'customerLabel',
      align: 'center',
      render: val => <span className="po-customer">{val}</span>,
      filters: (customers || []).map(c => ({ text: c.bi_name, value: c.bi_name })),
      onFilter: (value, record) => record.customerLabel.indexOf(value) === 0,
      sorter: (a, b) =>
        (a.customerLabel || '').localeCompare(b.customerLabel || ''),
    },
    {
      key: 'delivered',
      title: 'Items / Delivered / Qty',
      align: 'center',
      render: data => (
        <span className="po-items-chip">
          <Icon type="shopping" style={{ fontSize: 10 }} />
          {data.totalItems} item/s
          <span className="po-items-sep">|</span>
          {data.totalDelivered}
          <span className="po-items-sep">/</span>
          {data.totalQuantity}
        </span>
      ),
      sorter: (a, b) => a.totalQuantity - b.totalQuantity,
    },
    {
      key: 'currency',
      title: 'Currency',
      dataIndex: 'currency',
      align: 'center',
      render: val => <CurrencyBadge currency={val} />,
      sorter: (a, b) => (a.currency || '').localeCompare(b.currency || ''),
      filters: [
        { text: 'PHP', value: 'PHP' },
        { text: 'USD', value: 'USD' },
      ],
      onFilter: (value, record) => record.currency.indexOf(value) === 0,
    },
  ];

  return (
    <>
      <style>{tableStyles}</style>
      <Card
        bordered={false}
        bodyStyle={{ padding: 0 }}
        style={{
          borderRadius: 10,
          boxShadow: '0 2px 12px rgba(24,144,255,0.07)',
          border: '1px solid #e6f0ff',
          overflow: 'hidden',
          marginTop: 8,
        }}
      >
        {/* ── Header ── */}
        <div className="po-card-header">
          <Icon
            type="file-done"
            style={{ color: '#1890ff', fontSize: 16, animation: 'po-beat 1.8s ease-in-out infinite' }}
          />
          <span style={{ fontWeight: 700, fontSize: 13, color: '#262626', letterSpacing: '.5px' }}>
            PURCHASE ORDERS
          </span>
          <Badge
            count={pageLength || 0}
            style={{ backgroundColor: '#1890ff', fontSize: 11, fontWeight: 600 }}
            overflowCount={99999}
          />
          {/* ── Status summary pills ── */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {openCount > 0 && (
              <span className="po-badge po-open">
                <span className="po-dot" />{openCount} OPEN
              </span>
            )}
            {servedCount > 0 && (
              <span className="po-badge po-served">
                <span className="po-dot" />{servedCount} SERVED
              </span>
            )}
          </div>
        </div>

        {/* ── Table ── */}
        <Table
          className="po-table"
          rowKey="id"
          bordered
          size="small"
          columns={tableColumns}
          dataSource={poList}
          style={{ overflowX: 'auto', margin: 0 }}
          rowClassName={(_, idx) => (idx % 2 === 0 ? '' : 'po-row-stripe')}
          pagination={{
            total: pageLength,
            current: paginate?.page,
            pageSize: paginate?.pageSize,
            defaultPageSize: paginate?.pageSize,
            showSizeChanger: true,
            onChange: onPageChange,
            onShowSizeChange: onSizeChange,
            pageSizeOptions: ['10', '15', '25'],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} items`,
            position: 'both',
            style: { padding: '8px 16px', marginBottom: 0 },
          }}
        />
      </Card>
    </>
  );
};

export default PurchaseOrderTable;